import React, {ReactElement} from 'react'
import PageTitle from "../components/subcomponents/PageTitle";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UpdateCustomerForm, {CustomerFormValues} from "../components/forms/UpdateCustomerForm";
import {styled} from "@mui/material/styles";
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import ErrorExclamation from "../components/subcomponents/ErrorExclamation";
import CustomerService from "../services/CustomerService";
import {FormIds, Products} from "../constants";
import CustomTabPanel from "../components/CustomTablePanel";
import InCareNetHFProductForm, {InCareNetHFFormValues} from "../components/forms/productForms/InCareNetHFProductForm";
import InCareNetHFService from "../services/InCareNetHFService";
import InPaceProductForm, {InPaceProductFormValues} from "../components/forms/productForms/InPaceProductForm";
import InPaceService from "../services/InPaceService";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Snackbar,
  Tab,
  Tabs
} from "@mui/material";
import {ClientDevice, ClientICD, DbCustomer, NetECG, FeatureToggleStates} from "sharedlibs";
import {useTranslation} from "react-i18next";
import NetECGProductForm, {NetECGProductFormValues} from "../components/forms/productForms/NetECGProductForm";
import {NetECGService} from "../services/NetECGService";
import {updateFeatureSwitchStates} from "../api/software";
import {logBusinessAudit} from "../utils/auditLogHelper";
import CustomerSalespersonSection from "../components/customer/CustomerSalespersonSection";
import {useUserRole} from "../utils/UserRoleContext";

export const Spacer = styled("span")({
  width: "100%",
  height: "2rem",
  display: "block"
})

const featureToggleKeys: (keyof FeatureToggleStates)[] = ['inSuiteBilling', 'bayerProject', 'questionnaires', 'coverLetter']

// Returns only the toggles that differ between the baseline and the current value. Compares
// only the four real toggle fields — the objects handed around here can carry an extra runtime
// `fetchedAt` (from CachedFeatureToggleStates) that must not count as a change.
function changedFeatureStates(baseline: FeatureToggleStates, current: FeatureToggleStates): Partial<FeatureToggleStates> {
  const changed: Partial<FeatureToggleStates> = {}
  for (const key of featureToggleKeys) {
    if (baseline[key] !== current[key]) {
      changed[key] = current[key]
    }
  }
  return changed
}

type CustomerRegardingProducts = {
  inCareNetHF?: ClientICD[],
  inPace?: ClientDevice[],
  netECG?: NetECG[],
  [key: string]: ClientICD[] | ClientDevice[] | NetECG[] | undefined;
}

interface TabPanelComposit {
  tab: ReactElement,
  panel: ReactElement,
  productName: string,
  productId: string
}

interface ProductInfo {
  productName: string
  product: ClientICD | ClientDevice | NetECG
  productId: string
}

export default function UpdateCustomerPage() {

  const {id} = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const product = searchParams.get('product');
  const {pathname} = useLocation();
  const navigate = useNavigate();

  const fallbackURL = searchParams.get('fallbackURL')

  const [error, setError] = React.useState(false)
  const [formDirty, setFormDirty] = React.useState(false)
  const [dirtyProduct, setDirtyProduct] = React.useState(false)
  const [loading, setLoading] = React.useState(true)
  const [initialLoadingComplete, setInitialLoadingComplete] = React.useState(false)
  const [customer, setCustomer] = React.useState<DbCustomer | null>(null)
  const [customerRegardingProducts, setCustomerRegardingProducts] = React.useState<CustomerRegardingProducts>({})
  const [tabValue, setTabValue] = React.useState(0)

  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [snackbarMessage, setSnackbarMessage] = React.useState("")
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("success")

  const [featureStates, setFeatureStates] = React.useState<FeatureToggleStates>({
    inSuiteBilling: false,
    questionnaires: false,
    bayerProject: false,
    coverLetter: false
  })

  // The last feature toggle states actually confirmed on the ICN instance (from the initial
  // load, a manual "Fetch now", or our own last successful push) — the baseline Step 3 below
  // diffs against to decide whether the admin changed anything worth pushing.
  const initialFeatureStatesRef = React.useRef<FeatureToggleStates | null>(null)

  // Fires on every change to the *current* (possibly still unsaved) feature toggle value,
  // including a user flipping a switch. Must NOT touch initialFeatureStatesRef — that ref may
  // only move when onFeatureStatesLoaded below confirms a real sync with the ICN instance, or
  // it would silently swallow a pending admin edit (a fetch-now firing after an unsaved toggle
  // would otherwise appear to make that toggle's baseline "unchanged").
  function handleFeatureSwitchStatesUpdate(states: FeatureToggleStates) {
    setFeatureStates(states)
  }

  // Fires only when the form just loaded/synced states from the server (initial mount fetch or
  // a manual "Fetch now") — never from a user toggle. This is the only place allowed to move
  // the baseline, so a fetch-now mid-visit or our own post-save resync (see Step 3) is reflected
  // without ever mistaking an in-progress edit for a confirmed state.
  function handleFeatureStatesLoaded(states: FeatureToggleStates) {
    initialFeatureStatesRef.current = states
  }

  // Fires the instant an InCareNetHFProductForm mounts, before it has fetched anything. A
  // customer can have several ICN instance tabs, but featureStates/initialFeatureStatesRef are
  // shared across all of them — without this reset, switching tabs while the previous tab's
  // baseline is still in place (or the new tab's own load then failing) would leave that stale
  // baseline in place, and Step 3 below could push it to the newly active tab's solId. Setting
  // the baseline to null makes Step 3's guard (`initialFeatureStatesRef.current ? ... : {}`)
  // correctly skip the push until this tab's own load actually confirms a real one.
  function handleFeatureStatesReset() {
    setFeatureStates({inSuiteBilling: false, questionnaires: false, bayerProject: false, coverLetter: false})
    initialFeatureStatesRef.current = null
  }

  const {t} = useTranslation()
  const {isAdmin} = useUserRole()

  async function onCustomerFormSubmit(customerData: CustomerFormValues) {
    try {
      if (pathname === "/customers/add") {
        try {
          await CustomerService.addCustomer(customerData)
        await logBusinessAudit('audit.customer.added', '', null, customerData)
        setShowSnackbar(true)
        setSnackbarSeverity("success")
        setSnackbarMessage(t('form.fields.customer.snackbar.successfulAdding'))
        await new Promise(resolve => setTimeout(resolve, 1000))
        navigate("/customers")
        return
        } catch (e) {
          console.error(e)
          setShowSnackbar(true)
          setSnackbarSeverity("error")
          setSnackbarMessage(
            e instanceof Error && e.message
              ? `${t('form.fields.customer.snackbar.failedAddingUnique')}: ${e.message}`
              : t('form.fields.customer.snackbar.failedAdding')
          )
          return
        }
      }

      if (customer === null) {
        console.error("Could not update customer. Object is null")
        throw new Error()
      }

      await CustomerService.updateCustomer(customer, customerData)
      await logBusinessAudit('audit.customer.updated', customer.id ?? '', customer, customerData)

      setShowSnackbar(true)
      setSnackbarSeverity("success")
      setSnackbarMessage(t('form.fields.customer.snackbar.successfulUpdating'))

      if (fallbackURL) {
        navigate(`/${fallbackURL}`)
      }
    } catch (e) {
      setShowSnackbar(true)
      setSnackbarSeverity("error")
      setSnackbarMessage(t('form.customer.snackbar.failedUpdating'))
    }
  }

  function getInitialTabValue() {
    if (product) {
      const composite = getProducts()

      if (composite.length === 0) {
        return 0
      }

      const index = composite.findIndex((elem) => elem.productId === product)
      if (index === -1) {
        throw new Error(`ProductId: ${product} cannot be found`)
      }
      return index
    }
    return 0
  }

  async function loadData() {
    if (pathname === "/customer/add") {
      setLoading(false)
      return
    }

    if (pathname.includes("/customers/update") && id) {
      let customer
      try {
        customer = await CustomerService.getCustomerById(id)
        const regardingProducts: CustomerRegardingProducts = {}
        for (const productType of Products) {
          regardingProducts[productType.name] = await productType.getData({
            customerId: id
          })
        }
        setCustomerRegardingProducts(regardingProducts)
      } catch (e) {
        console.error(e)
      }
      if (customer) {
        setCustomer(customer)
      } else {
        setError(true)
      }
    }
    setLoading(false)
  }

  function handleTabChange(event: React.SyntheticEvent, newValue: number) {
    setTabValue(newValue)
  }

  function getDefaultCustomerFormValues(): CustomerFormValues {
    const defaultFormConfig = {
      name: '',
      doctorName: '',
      customerNumber: '',
      streetAndDwellingNumber: '',
      zip: '',
      location: '',
      biotronikClientName: '',
      biotronikCustomerNumber: '',
      eTin: '',
      bostonSAPID: '',
      abbottId: '',
      microportId: '',
      system: '',
      nonGbaGroupName: '',
      gbaGroupName: '',
      billingInHfGroups: false,
      hfBillingGroups: [],
    }

    if (pathname === "/customer/add") {
      return defaultFormConfig
    }

    return customer === null ? defaultFormConfig : CustomerService.mapCustomerObjectToFormObject(customer)
  }

  function getProductFormId() {
    const tabs = getProducts()
    if (tabs.length === 0) {
      return ""
    }
    switch (tabs[tabValue].productName) {
      case "inCareNetHF":
        return FormIds.updateInCareNetHF
      case "inPace":
        return FormIds.updateInPace
      case "netECG":
        return FormIds.updateNetECG
      default:
        return ""
    }
  }

  React.useEffect(() => {
    loadData().then(() => {
      setInitialLoadingComplete(true)
    })
  }, [tabValue])

  React.useEffect(() => {
    setTabValue(getInitialTabValue())
  }, [initialLoadingComplete])

  function renderCustomerDetails() {
    return error ?
      <ErrorExclamation message="Error while fetching customer details"/> :
      <UpdateCustomerForm onSubmit={onCustomerFormSubmit} defaultValues={getDefaultCustomerFormValues()}
                          updateDirty={setFormDirty}/>
  }

  async function handleProductFormSubmit(formObject: InCareNetHFFormValues | InPaceProductFormValues | NetECGProductFormValues) {
    const tabs = getProducts()
    if (!id) {
      return
    }

    switch (tabs[tabValue].productName) {
      case "inCareNetHF": {
        // Step 1: save form to DB — if this fails, nothing was persisted so no audit needed
        try {
          await InCareNetHFService.updateInCareNetHF(tabs[tabValue].product as ClientICD, formObject as InCareNetHFFormValues)
        } catch (e) {
          console.error(e)
          setShowSnackbar(true)
          setSnackbarSeverity("error")
          setSnackbarMessage(t('form.fields.inCareNet.errorUpdating'))
          break
        }

        // Step 2: log after successful DB save — normalize prevValue to form shape so
        // date fields (tmzActiveSince, plannedDeactivationDate, …) use the same dayjs
        // serialization as formObject, avoiding false "changed" diffs
        const prevFormValues = InCareNetHFService.mapInCareNetHFObjectToFormObject(tabs[tabValue].product as ClientICD)
        await logBusinessAudit(
          'audit.inCareNetHF.updated',
          id ?? '',
          { ...prevFormValues, ...(initialFeatureStatesRef.current ?? {}) },
          { ...formObject as InCareNetHFFormValues, ...featureStates },
        )

        // Step 3: push feature switches to ICN — but only if the admin actually changed
        // one (featureStates differs from the last known-good state the form loaded).
        // Without this guard, saving any unrelated field (price, url, ...) would also
        // re-push whatever featureStates currently holds — including the all-false
        // placeholder used before any real data was ever fetched (see
        // InCareNetHFProductForm's "no data yet" state) — silently disabling features
        // that are actually still enabled on the instance. Only the changed toggles are
        // sent: the displayed states come from the DB cache, which can be out of date, and
        // the instance leaves every toggle that is not in the request as it is.
        // Failures are silently ignored (instance may be disabled, making the datasafe
        // unreachable by design).
        const changedToggles = initialFeatureStatesRef.current
          ? changedFeatureStates(initialFeatureStatesRef.current, featureStates)
          : {}
        if (pathname !== "/customer/add" && Object.keys(changedToggles).length > 0) {
          try {
            await updateFeatureSwitchStates((tabs[tabValue].product as ClientICD).id!, changedToggles)
            // The push just succeeded, so featureStates is now the confirmed state on the ICN
            // instance — move the baseline forward so an unrelated save right after this one
            // doesn't re-push the same (now unchanged) states again.
            initialFeatureStatesRef.current = featureStates
          } catch (e) {
            console.error(e)
          }
        }

        setShowSnackbar(true)
        setSnackbarSeverity("success")
        setSnackbarMessage(t('form.fields.inCareNet.successfullyUpdated'))
        break
      }
      case "inPace":
        try {
          await InPaceService.updateDevice(tabs[tabValue].product as ClientDevice, formObject as InPaceProductFormValues)
          await logBusinessAudit('audit.inPace.updated', id ?? '', tabs[tabValue].product, formObject)

          setShowSnackbar(true)
          setSnackbarSeverity("success")
          setSnackbarMessage(t('form.fields.inPace.sucessfullyUpdated'))
        } catch (e) {
          console.error(e)
          setShowSnackbar(true)
          setSnackbarSeverity("error")
          setSnackbarMessage(t('form.fields.inPace.errorUpdating'))
        }
        break
      case "netECG":
        try {
          await NetECGService.updateNetECG(tabs[tabValue].product as NetECG, formObject as NetECGProductFormValues)
          await logBusinessAudit('audit.netECG.updated', id ?? '', tabs[tabValue].product, formObject)

          setShowSnackbar(true)
          setSnackbarSeverity("success")
          setSnackbarMessage("NetECG-Produkt erfolgreich geupdated")
        } catch (e: any) {
          console.error(e)
          setShowSnackbar(true)
          setSnackbarSeverity("error")
          setSnackbarMessage("Fehler beim Updaten des NetECG-Produkts")
        }
        break;
      default:
        return
    }

    if (fallbackURL) {
      navigate(`/${fallbackURL}`)
    }
  }

  function getProducts(): ProductInfo[] {
    const products: ProductInfo[] = []

    if (Object.keys(customerRegardingProducts).length === 0) {
      return products
    }

    Products.forEach((productType) => {
      const productList = customerRegardingProducts[productType.name] ?? []
      productList.forEach((product) => {
        if (!product.id) {
          throw Error("ProductId could not be gathered")
        }
        products.push({
          productName: productType.name,
          product: product,
          productId: product.id
        })
      })
    })
    return products
  }

  function getProductTabPanelComposite(products: ProductInfo[]): TabPanelComposit[] {
    let tabsAndPanels: TabPanelComposit[] = []

    if (products.length === 0) {
      return tabsAndPanels
    }

    const groupedItems = products.reduce<Record<string, ProductInfo[]>>((acc, item) => {
      const productName = item.productName;

      if (!acc[productName]) {
        acc[productName] = [];
      }

      acc[productName].push(item);

      return acc;
    }, {});


    const splitProducts = Object.values(groupedItems)

    splitProducts.forEach((elemArr, productGroupIndex) => {
      elemArr.forEach((elem, productIndex) => {
        let panelForm

        switch (elem.productName) {
          case "inCareNetHF":
            panelForm = (<InCareNetHFProductForm setFeatureSwitchStates={handleFeatureSwitchStatesUpdate}
                                                 onFeatureStatesLoaded={handleFeatureStatesLoaded}
                                                 onFeatureStatesReset={handleFeatureStatesReset} isCreate={false}
                                                 onSubmit={handleProductFormSubmit}
                                                 defaultValues={InCareNetHFService.mapInCareNetHFObjectToFormObject(elem.product as ClientICD)}
                                                 updateDirty={setDirtyProduct}/>)
            break
          case "inPace":
            panelForm = (<InPaceProductForm onSubmit={handleProductFormSubmit}
                                            defaultValues={InPaceService.mapDeviceObjectToFormObject(elem.product as ClientDevice)}
                                            updateDirty={setDirtyProduct}/>)
            break
          case "netECG":
            panelForm = (<NetECGProductForm onSubmit={handleProductFormSubmit} updateDirty={setDirtyProduct}
                                            defaultValues={elem.product as NetECG}/>)
            break;
          default:
            break
        }

        let panelIndex: number
        if (productGroupIndex === 0) {
          panelIndex = productIndex
        } else {
          // Sum the amount of elements in the prior product groups
          let sum = 0
          for (let i = 0; i < productGroupIndex; i++) {
            sum += splitProducts[i].length
          }
          panelIndex = sum + productIndex
        }

        tabsAndPanels.push({
          tab: (<Tab label={`${elem.productName} ${productIndex + 1}`} key={elem.productId}/>),
          panel: (
            <CustomTabPanel index={panelIndex} value={tabValue} key={elem.productId}>
              {panelForm}
            </CustomTabPanel>
          ),
          productName: elem.productName,
          productId: elem.productId
        })
      })
    })
    return tabsAndPanels
  }

  return (
    <div className={"base-container"}>
      <PageTitle>{t('form.updateCustomerForm.editData')}</PageTitle>
      <Divider/>
      <Spacer/>
      {/* Customer section*/}
      <Accordion defaultExpanded={!product}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon/>}
          id="update-customer-panel"
        >
          {t('form.updateCustomerForm.customerData')}
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{paddingBottom: "1rem"}}>
            {loading ?
              <CircularProgress/> :
              renderCustomerDetails()
            }
          </Box>
        </AccordionDetails>
        <AccordionActions>
          <Button
            sx={{marginRight: ".5rem"}}
            type="submit"
            form={FormIds.updateCustomer}
            disabled={!formDirty}
          >{t('activities.save')}</Button>
        </AccordionActions>
      </Accordion>

      {/* Salesperson section — only on edit (we need the customer.id to call the assign endpoint) */}
      {pathname !== "/customers/add" && customer && (
        <>
          <Spacer/>
          <CustomerSalespersonSection
            customer={customer}
            onChanged={(updated) => setCustomer(updated)}
            disabled={!isAdmin}
          />
        </>
      )}

      <Spacer/>
      {/* Product section*/}
      {
        pathname === "/customers/add" ? <div/> :
          <Accordion defaultExpanded={!!product}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
              id="update-products-panel"
            >
              {t('form.updateCustomerForm.productData')}
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  {getProductTabPanelComposite(getProducts()).map((elem) => {
                    return elem.tab
                  })}
                </Tabs>
              </Box>
              {getProductTabPanelComposite(getProducts()).map((elem) => {
                return elem.panel
              })}
            </AccordionDetails>
            <AccordionActions>
              <Button
                sx={{marginRight: ".5rem"}}
                type="submit"
                form={getProductFormId()}
                disabled={!dirtyProduct}
              >{t('activities.save')}</Button>
            </AccordionActions>
          </Accordion>
      }

      <Snackbar
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        autoHideDuration={6000}
        anchorOrigin={{vertical: "bottom", horizontal: "center"}}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity={snackbarSeverity as "success" | "error"}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </div>
  )
}
